import { HocuspocusProvider } from '@hocuspocus/provider';
import { collaborativeWsUrl } from 'config';
import randomColor from 'randomcolor';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TDAsset, TDBinding, TDShape, TldrawApp } from 'sn-wb-tldraw';
import { useAuth } from 'src/shared/hooks/use-auth';
import * as Y from 'yjs';

import { useLocalStorage } from './useLocalStorage';

export function useWhiteboardService(roomId: string, memberId: string) {
  const [app, setApp] = useState<TldrawApp>();
  const [users, setUsers] = useState<any>([]);
  const [user, setUser] = useState<any>();
  const { saveDataToLocalStorage } = useLocalStorage();
  const userDetails = useAuth((s) => s.userDetails);
  const [isSyncing, setIsSyncing] = useState(true);
  const [spotLightUser, setSpotlightUser] = useState<string>('');

  const provider = useMemo(
    () =>
      new HocuspocusProvider({
        url: collaborativeWsUrl,
        onSynced: () => {
          setIsSyncing(false);
        },
        parameters: {
          type: 'whiteboard',
          member: memberId,
        },
        name: roomId,
      }),
    [roomId]
  );

  const [yShapes, yBindings, yAssets, ySections, undoManager, doc, awareness, ySpotlightUser] =
    useMemo(() => {
      const _yShapes: Y.Map<TDShape> = provider.document.getMap('shapes');
      const _yBindings: Y.Map<TDBinding> = provider.document.getMap('bindings');
      const _yAssets: Y.Map<TDAsset> = provider.document.getMap('assets');
      const _ySections: Y.Map<any> = provider.document.getMap('sections');
      const _undoManager = new Y.UndoManager([_yShapes, _yBindings, _yAssets, _ySections]);
      const ySpotlightUser = provider.document.getText('spotlightUser');
      const awareness = provider.awareness;

      return [
        _yShapes,
        _yBindings,
        _yAssets,
        _ySections,
        _undoManager,
        provider.document,
        awareness,
        ySpotlightUser,
      ];
    }, [provider.document]);

  const updateApp = useCallback(() => {
    if (!(yShapes || yBindings || ySections)) return;
    app?.replacePageContent(
      Object.fromEntries(yShapes.entries()),
      Object.fromEntries(yBindings.entries()),
      Object.fromEntries(yAssets.entries()),
      Object.fromEntries(ySections.entries())
    );
  }, [app, yShapes, yBindings, yAssets, ySections]);

  const updateUserPoint = useCallback((point: number[], cameraPoint: number[], zoom: [number]) => {
    awareness.setLocalStateField('point', point);
    awareness.setLocalStateField('cameraPoint', cameraPoint);
    awareness.setLocalStateField('zoom', zoom);
    setUser(awareness.getLocalState());
  }, []);

  const updateUserFollowing = useCallback((userId: string, point: number[]) => {
    awareness.setLocalStateField('following', userId);
    awareness.setLocalStateField('point', point);
    setUser(awareness.getLocalState());
  }, []);

  const updateSpotlight = useCallback(() => {
    setSpotlightUser(ySpotlightUser.toString());
  }, [app, ySpotlightUser]);

  const updateSpotlightUser = (id: string) => {
    doc.transact(() => {
      ySpotlightUser.delete(0, ySpotlightUser.length);
      ySpotlightUser.insert(0, `${id}`);
    });
  };
  const onMount = useCallback(
    (app: TldrawApp) => {
      app.loadRoom(roomId);
      app?.setSetting('showGrid', true);
      // window.app = app;
      app.pause();
      setApp(app);
      app?.replacePageContent(
        Object.fromEntries(yShapes.entries()),
        Object.fromEntries(yBindings.entries()),
        Object.fromEntries(yAssets.entries()),
        Object.fromEntries(ySections.entries())
      );
    },
    [roomId, updateApp]
  );

  const onChangePage = (
    app: TldrawApp,
    shapes: Record<string, TDShape | undefined>,
    bindings: Record<string, TDBinding | undefined>,
    assets: Record<string, TDAsset | undefined>,
    sections: Record<string, any | undefined>
  ) => {
    if (!(yShapes && yBindings)) return;
    undoManager.stopCapturing();
    const newData = {
      shapes: Object.fromEntries(yShapes.entries()),
      bindings: Object.fromEntries(yBindings.entries()),
      assets: Object.fromEntries(yAssets.entries()),
      sections: Object.fromEntries(ySections.entries()),
      state: Array.from(Y.encodeStateAsUpdate(doc)),
    };
    saveDataToLocalStorage(roomId, newData);
    doc.transact(() => {
      Object.entries(shapes).forEach(([id, shape]) => {
        if (!shape) {
          yShapes.delete(id);
        } else {
          yShapes.set(shape.id, shape);
        }
      });
      Object.entries(bindings).forEach(([id, binding]) => {
        if (!binding) {
          yBindings.delete(id);
        } else {
          yBindings.set(binding.id, binding);
        }
      });
      Object.entries(assets).forEach(([id, asset]) => {
        if (!asset) {
          yAssets.delete(id);
        } else {
          yAssets.set(asset.id, asset);
        }
      });
      Object.keys(sections).forEach((section) => {
        if (!section) {
          ySections.delete(section);
        } else {
          ySections.set(section, sections[section]);
        }
      });
    });
  };

  const onUndo = useCallback(() => {
    undoManager.undo();
  }, []);

  const onRedo = useCallback(() => {
    undoManager.redo();
  }, []);

  useEffect(() => {
    const updateUsersState = () => {
      const activeUsers = Array.from(awareness.getStates().values()).filter(
        (value) => Object.keys(value).length !== 0
      );
      setUsers(activeUsers);
    };

    awareness.on('change', updateUsersState);

    const user = {
      id: awareness.clientID,
      point: [0, 0],
      color: randomColor({
        luminosity: 'dark',
        alpha: 1,
        format: 'hex',
      }),
      following: '',
      user: { name: userDetails?.name || '', avatar: userDetails?.avatarUrl || '' },
      cameraPoint: [0, 0],
      zoom: 1,
    };

    awareness.setLocalState(user);
    setUser(awareness.getLocalState());

    return () => {
      awareness.off('change', () => {
        updateUsersState();
        setUser({});
      });
    };
  }, []);

  useEffect(() => () => provider.disconnect(), [provider]);

  useEffect(() => {
    yShapes.observeDeep(updateApp);
    yBindings.observeDeep(updateApp);
    yAssets.observeDeep(updateApp);
    ySections.observeDeep(updateApp);

    return () => {
      yShapes.unobserveDeep(updateApp);
    };
  }, [updateApp]);

  useEffect(() => {
    ySpotlightUser.observe(updateSpotlight);
    return () => ySpotlightUser.unobserve(updateSpotlight);
  }, [updateSpotlight]);

  return {
    user,
    users,
    updateUserPoint,
    updateUserFollowing,
    onMount,
    onChangePage,
    onUndo,
    onRedo,
    isSyncing,
    updateSpotlightUser,
    spotLightUser,
  };
}
